/**
 * The public header on the portal's way-in pages (Claude Design's copy of 13 Oct 2026, 20:33 UTC: the Sign up / Log in
 * screen replaced its 2026-10-11 header with the `.wh` header of the Landing, Marketing and Status screens — "handoff 13
 * Oct 2026"). The portal's copy of the public site's `components/SiteHeader.astro` and `site/site-header.ts` (the apps
 * never import each other): the same markup, classes and behaviour, drawn by React; the look is `site-header.css`, the
 * entries `site-header-model.ts`, the words the core catalog's `site_header.*`.
 *
 * At 64rem and wider — the bar: the wordmark, Solutions (a mega menu of industries and use cases with Live and Soon
 * badges, and the "Hear it work" panel), Product and Resources (dropdowns), Pricing, then Log in, Start free and the
 * language menu. One panel at a time, from its button (`aria-expanded`), over the design's scrim (dark under Solutions,
 * light under the other two); a second press, a press outside, a followed link or Escape closes it — Escape returns
 * focus to its button.
 *
 * Below 64rem — the wordmark, Start free and ☰, which opens a full-screen menu: Solutions (with Industries and Use cases
 * tabs), Product and Resources as screens of their own (focus to the screen's title; Back returns to the row that opened
 * it), Pricing, and at its foot Log in, "Start free — 30 minutes on us" and the language menu (first screen only). Tab
 * stays inside it, Escape closes it (unless the language list took that Escape), a followed link closes it, closing
 * returns focus to ☰, and the page under it does not scroll. Crossing the 64rem line closes whatever belongs to the
 * other width.
 *
 * AMENDED 2026-10-17 (plan-020 AP2-FIX): the phone menu's Pricing line is the site's "<rate> a minute, or less on a plan"
 * (design §32.12.2 A-1), the rate the api's standard pay-as-you-go read (`PricingLine`), never a literal.
 *
 * AMENDED 2026-10-15 (design audit against Claude Design's copy of 14 Oct 2026, 23:17 UTC): an entry marked Soon opens
 * the design's waitlist dialog (`waitlist-dialog.tsx`, stored through `joinWaitlist` as on the public site), its link to
 * the Contact page kept for a press with a modifier; on the phone menu its badge reads "Waitlist", as the design draws it.
 *
 * Every entry is a page of wetalk.io (`usePublicSiteLink`, the Greek pages for a Greek reader); Log in and Start free
 * are this portal's routes, the current one marked by the router's `aria-current="page"`, and a signed-in person
 * (creating an organization, opening an invitation) is not offered them.
 */
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";

import type { WaitlistSignupRequest } from "../../api/slice/personal-security.js";
import { standardTalkRateHeadlineOf, useStandardTalkRates } from "../../api/slice/talk-rate.js";

import { portalText, usePortalTranslation, type PortalKey } from "../../i18n/portal.js";
import { formatMoney } from "../../money.js";

import { HeaderLanguageMenu } from "./header-language-menu.js";
import {
  HEADER_CONNECT,
  HEADER_CONTACT_PATH,
  HEADER_DEMO_PATH,
  HEADER_ICON,
  HEADER_INDUSTRY,
  HEADER_LIBRARY_PATH,
  HEADER_NARROW_QUERY,
  HEADER_PLATFORM,
  HEADER_PRICING_PATH,
  HEADER_PRODUCT_NARROW,
  HEADER_RESOURCE,
  HEADER_USE_CASE,
  type HeaderLink,
  type HeaderSolution,
} from "./site-header-model.js";
import "./site-header.css";
import { WaitlistDialog, type WaitlistOpening } from "./waitlist-dialog.js";

/** The root class that locks the page's scroll while the phone menu is open (`public-frame.css`). */
export const HEADER_SCROLL_LOCK = "pd-scroll-locked";

const MOBILE_ID = "wh-mmenu";
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Panel = "solutions" | "product" | "resources";
type Screen = "main" | Panel;
type Tab = "industries" | "useCases";

const SOLUTION_TAB = [
  { id: "industries", label: "site_header.industries", entries: HEADER_INDUSTRY },
  { id: "useCases", label: "site_header.use_cases", entries: HEADER_USE_CASE },
] as const satisfies readonly { readonly id: Tab; readonly label: PortalKey<"core">; readonly entries: readonly HeaderSolution[] }[];

const solutionName = (entry: HeaderSolution): string => portalText("core", `site_header.solution.${entry.id}.name`);
const solutionDescription = (entry: HeaderSolution): string => portalText("core", `site_header.solution.${entry.id}.description`);
const linkName = (entry: HeaderLink): string => portalText("core", `site_header.link.${entry.id}.name`);
const linkDescription = (entry: HeaderLink): string => portalText("core", `site_header.link.${entry.id}.description`);

/**
 * The phone menu's Pricing line, as wetalk.io words it (design §32.12.2 A-1): "<standard rate> a minute, or less on a
 * plan" — the standard pay-as-you-go rate from the api (`/v1/public/talk-rates`), "from" the lowest once the kinds
 * differ; no figure, and so no line, until it is read (plan-020 AP2-FIX: no literal price).
 */
function PricingLine(): ReactNode {
  usePortalTranslation("core");
  const read = useStandardTalkRates();
  const headline = read.data === undefined ? null : standardTalkRateHeadlineOf(read.data);
  if (headline === null) return null;
  return portalText("core", headline.from ? "site_header.pricing_sub_from" : "site_header.pricing_sub", { value0: formatMoney(headline.perMinute) });
}

function Chevron({ className, d }: { readonly className: string; readonly d: string }): ReactNode {
  return (
    <svg className={className} stroke="currentColor" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

function RowIcon({ d }: { readonly d: string }): ReactNode {
  return (
    <svg className="wh-icon wh-ico" stroke="currentColor" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

function Wordmark({ href }: { readonly href: string }): ReactNode {
  return (
    <a href={href} aria-label={portalText("core", "public_frame.home")} className="wh-wm">
      <span className="wh-we">{portalText("core", "public_frame.wordmark_we")}</span>
      <span>{portalText("core", "public_frame.wordmark_talk")}</span>
    </a>
  );
}

/** Shown and reachable: not inside anything `hidden`. */
function reachable(element: HTMLElement): boolean {
  return element.closest("[hidden]") === null;
}

export interface SiteHeaderProps {
  /** `usePublicSiteLink()`: a path of wetalk.io as an address on the public site, in the portal's language. */
  readonly site: (path: string) => string;
  /** A signed-in person is not offered Log in and Start free. */
  readonly signedIn: boolean;
}

export function SiteHeader({ site, signedIn }: SiteHeaderProps): ReactNode {
  usePortalTranslation("core");
  const [open, setOpen] = useState<Panel | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [screen, setScreen] = useState<Screen>("main");
  const [tab, setTab] = useState<Tab>("industries");
  const [waitlist, setWaitlist] = useState<WaitlistOpening | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const burgerRef = useRef<HTMLButtonElement | null>(null);
  const closerRef = useRef<HTMLButtonElement | null>(null);
  /** Where focus goes once the next state is drawn (a screen's title, the row that opened it, ☰ or the close button). */
  const pendingFocus = useRef<(() => void) | null>(null);

  useEffect(() => {
    const focus = pendingFocus.current;
    pendingFocus.current = null;
    focus?.();
  });

  const toggleOf = (name: Panel): HTMLElement | null => rootRef.current?.querySelector<HTMLElement>(`[data-wh-btn="${name}"]`) ?? null;
  const closePanel = (focusToggle: boolean): void => {
    if (open === null) return;
    const was = open;
    setOpen(null);
    if (focusToggle) pendingFocus.current = () => toggleOf(was)?.focus();
  };
  const openMobile = (): void => {
    setOpen(null);
    setScreen("main");
    setTab("industries");
    setMobileOpen(true);
    pendingFocus.current = () => closerRef.current?.focus();
  };
  const closeMobile = (returnFocus: boolean): void => {
    setMobileOpen(false);
    setScreen("main");
    if (returnFocus) pendingFocus.current = () => burgerRef.current?.focus({ preventScroll: true });
  };
  const showScreen = (next: Screen): void => {
    const was = screen;
    setScreen(next);
    if (next === "solutions") setTab("industries");
    pendingFocus.current =
      next === "main"
        ? () => dialogRef.current?.querySelector<HTMLElement>(`[data-wh-row="${was}"]`)?.focus()
        : () => dialogRef.current?.querySelector<HTMLElement>(`[data-wh-screen="${next}"] [data-wh-scrtitle]`)?.focus();
  };

  // The document: Escape, the phone menu's focus trap, a press outside an open panel, and the 64rem line.
  useEffect(() => {
    if (open === null && !mobileOpen) return undefined;
    const trap = (event: KeyboardEvent): void => {
      const dialog = dialogRef.current;
      if (dialog === null) return;
      const inside = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(reachable);
      const first = inside[0];
      const last = inside[inside.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (!(active instanceof Node) || !dialog.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      // By document order, so an element that has just been hidden (the language menu's own list) still counts.
      const after = inside.some((each) => active.compareDocumentPosition(each) & Node.DOCUMENT_POSITION_FOLLOWING);
      const before = inside.some((each) => active.compareDocumentPosition(each) & Node.DOCUMENT_POSITION_PRECEDING);
      if (event.shiftKey && !before) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && !after) {
        event.preventDefault();
        first.focus();
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (mobileOpen) {
        if (event.key === "Tab") trap(event);
        else if (event.key === "Escape" && !event.defaultPrevented) {
          event.preventDefault();
          closeMobile(true);
        }
        return;
      }
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        closePanel(true);
      }
    };
    const onClick = (event: MouseEvent): void => {
      if (open === null || !(event.target instanceof Element)) return;
      // A panel's own button decides for itself (a second press closes, another button swaps).
      if (event.target.closest("[data-wh-btn]") !== null) return;
      if (event.target.closest(`[data-wh-panel="${open}"]`) !== null) return;
      closePanel(false);
    };
    const narrow = window.matchMedia(HEADER_NARROW_QUERY);
    const onWidth = (): void => {
      if (narrow.matches) closePanel(false);
      else closeMobile(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    narrow.addEventListener("change", onWidth);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
      narrow.removeEventListener("change", onWidth);
    };
  });

  // The page under the phone menu does not scroll; the scrollbar's width is kept as padding, so nothing moves.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const html = document.documentElement;
    const scrollbar = Math.max(0, window.innerWidth - html.clientWidth);
    html.style.setProperty("--pd-scrollbar-width", `${String(scrollbar)}px`);
    html.classList.add(HEADER_SCROLL_LOCK);
    return () => {
      html.classList.remove(HEADER_SCROLL_LOCK);
      html.style.removeProperty("--pd-scrollbar-width");
    };
  }, [mobileOpen]);

  const toggle = (name: Panel) => (): void => {
    setOpen((was) => (was === name ? null : name));
  };
  const onPanelClick = (event: ReactMouseEvent<HTMLElement>): void => {
    if (event.target instanceof Element && event.target.closest("a[href]") !== null) setOpen(null);
  };
  const onDialogClick = (event: ReactMouseEvent<HTMLElement>): void => {
    if (event.target instanceof Element && event.target.closest("a[href]") !== null) closeMobile(false);
  };

  /** A soon entry opens the waitlist dialog (a press with a modifier still follows its link to the Contact page). */
  const joinWaitlist = (entry: HeaderSolution, source: WaitlistSignupRequest["source"]) => (event: ReactMouseEvent<HTMLAnchorElement>): void => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    setWaitlist({ entry, source, opener: event.currentTarget });
  };

  const panelButton = (name: Panel, label: PortalKey<"core">): ReactNode => (
    <button type="button" className="wh-item" data-wh-btn={name} aria-expanded={open === name} aria-controls={`wh-p-${name}`} onClick={toggle(name)}>
      {portalText("core", label)}
      <Chevron className="wh-icon wh-chev" d="M6 9l6 6 6-6" />
    </button>
  );
  const dropItem = (entry: HeaderLink): ReactNode => (
    <a key={entry.id} href={site(entry.path)} className="wh-di">
      <span className="wh-di-name">{linkName(entry)}</span>
      <span className="wh-di-desc">{linkDescription(entry)}</span>
    </a>
  );
  const screenTop = (name: Panel, title: PortalKey<"core">): ReactNode => (
    <div className="wh-stop">
      <button type="button" className="wh-back" aria-label={portalText("core", "site_header.back_to_menu")} onClick={() => showScreen("main")}>
        <svg className="wh-icon" stroke="currentColor" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        {portalText("core", "site_header.back")}
      </button>
      <h2 className="wh-stitle" tabIndex={-1} data-wh-scrtitle={name}>
        {portalText("core", title)}
      </h2>
      <span />
    </div>
  );

  return (
    <header className="wh" ref={rootRef}>
      <div className="wh-scrim" hidden={open === null} data-tone={open ?? undefined} aria-hidden="true" />

      <div className="wh-bar">
        <div className="wh-in">
          <Wordmark href={site("/")} />
          <nav className="wh-items" aria-label={portalText("core", "site_header.main")}>
            <div className="wh-dd wh-dd-mega">
              {panelButton("solutions", "site_header.solutions")}
              <div id="wh-p-solutions" className={open === "solutions" ? "wh-mega is-open" : "wh-mega"} data-wh-panel="solutions" onClick={onPanelClick}>
                <div className="wh-cols">
                  {SOLUTION_TAB.map((column) => (
                    <div key={column.id} className="wh-col">
                      <p className="wh-colh">{portalText("core", column.label)}</p>
                      <div className="wh-list">
                        {column.entries.map((entry) => (
                          <a
                            key={entry.id}
                            href={site(entry.path)}
                            className="wh-mi"
                            {...(entry.status === "soon" ? { "aria-haspopup": "dialog" as const, onClick: joinWaitlist(entry, "nav_desktop") } : {})}
                          >
                            <span className="wh-mi-top">
                              <span className="wh-mi-name">{solutionName(entry)}</span>
                              {entry.status === "live" ? (
                                <span className="wh-live">
                                  <span className="wh-dot" />
                                  {portalText("core", "site_header.live")}
                                </span>
                              ) : (
                                <span className="wh-soon">{portalText("core", "site_header.soon")}</span>
                              )}
                            </span>
                            <span className="wh-mi-desc">{solutionDescription(entry)}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="wh-demo">
                    <p className="wh-demo-eye">{portalText("core", "site_header.hear")}</p>
                    <p className="wh-demo-title">{portalText("core", "site_header.demo_title")}</p>
                    <a href={site(HEADER_DEMO_PATH)} className="wh-play">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M6 3.5v17l14-8.5z" />
                      </svg>
                      {portalText("core", "site_header.play")}
                    </a>
                  </div>
                </div>
                <div className="wh-foot">
                  <a href={site(HEADER_LIBRARY_PATH)} className="wh-foot-all">
                    {portalText("core", "site_header.see_all")}
                  </a>
                  <span className="wh-foot-r">
                    {portalText("core", "site_header.not_sure")}{" "}
                    <a href={site(HEADER_CONTACT_PATH)} className="wh-talk">
                      {portalText("core", "site_header.talk")}
                    </a>
                  </span>
                </div>
              </div>
            </div>
            <div className="wh-dd">
              {panelButton("product", "site_header.product")}
              <div id="wh-p-product" className={open === "product" ? "wh-drop wh-drop-2 is-open" : "wh-drop wh-drop-2"} data-wh-panel="product" onClick={onPanelClick}>
                <div>
                  <p className="wh-dh">{portalText("core", "site_header.platform")}</p>
                  {HEADER_PLATFORM.map(dropItem)}
                </div>
                <div>
                  <p className="wh-dh">{portalText("core", "site_header.connect")}</p>
                  {HEADER_CONNECT.map(dropItem)}
                </div>
              </div>
            </div>
            <a href={site(HEADER_PRICING_PATH)} className="wh-item wh-price">
              {portalText("core", "site_header.pricing")}
            </a>
            <div className="wh-dd">
              {panelButton("resources", "site_header.resources")}
              <div id="wh-p-resources" className={open === "resources" ? "wh-drop wh-drop-1 is-open" : "wh-drop wh-drop-1"} data-wh-panel="resources" onClick={onPanelClick}>
                {HEADER_RESOURCE.map(dropItem)}
              </div>
            </div>
          </nav>
          <div className="wh-act">
            {signedIn ? null : (
              <>
                <Link to="/log-in" className="wh-login">
                  {portalText("core", "auth.log_in_f7c400")}
                </Link>
                <Link to="/sign-up" className="wh-start">
                  {portalText("core", "public_frame.start_free")}
                </Link>
              </>
            )}
            <HeaderLanguageMenu menuId="wh-p-language" />
          </div>
        </div>
      </div>

      <div className="wh-mbar">
        <Wordmark href={site("/")} />
        <span className="wh-mact">
          {signedIn ? null : (
            <Link to="/sign-up" className="wh-mstart">
              {portalText("core", "public_frame.start_free")}
            </Link>
          )}
          <button
            type="button"
            ref={burgerRef}
            className="wh-burger"
            aria-label={portalText("core", "public_frame.open_menu")}
            aria-expanded={mobileOpen}
            aria-controls={MOBILE_ID}
            onClick={openMobile}
          >
            <svg className="wh-icon" stroke="currentColor" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 12h16M4 6h16M4 18h16" />
            </svg>
          </button>
        </span>
      </div>

      <div
        id={MOBILE_ID}
        ref={dialogRef}
        className={mobileOpen ? "wh-mmenu is-open" : "wh-mmenu"}
        role="dialog"
        aria-modal="true"
        aria-label={portalText("core", "site_header.menu")}
        onClick={onDialogClick}
      >
        <div className="wh-mtop">
          <Wordmark href={site("/")} />
          <button
            type="button"
            ref={closerRef}
            className="wh-burger"
            aria-label={portalText("core", "public_frame.close_menu")}
            aria-expanded="true"
            aria-controls={MOBILE_ID}
            onClick={() => closeMobile(true)}
          >
            <svg className="wh-icon" stroke="currentColor" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="wh-mbody">
          <div className="wh-s1" data-wh-screen="main" hidden={screen !== "main"}>
            <button type="button" className="wh-row" data-wh-row="solutions" onClick={() => showScreen("solutions")}>
              <span>
                <span className="wh-row-t">{portalText("core", "site_header.solutions")}</span>
                <span className="wh-row-s">{portalText("core", "site_header.solutions_sub")}</span>
              </span>
              <RowIcon d="M9 18l6-6-6-6" />
            </button>
            <button type="button" className="wh-row" data-wh-row="product" onClick={() => showScreen("product")}>
              <span>
                <span className="wh-row-t">{portalText("core", "site_header.product")}</span>
                <span className="wh-row-s">{portalText("core", "site_header.product_sub")}</span>
              </span>
              <RowIcon d="M9 18l6-6-6-6" />
            </button>
            <a href={site(HEADER_PRICING_PATH)} className="wh-row">
              <span>
                <span className="wh-row-t">{portalText("core", "site_header.pricing")}</span>
                <span className="wh-row-s">
                  <PricingLine />
                </span>
              </span>
              <RowIcon d="M5 12h14M12 5l7 7-7 7" />
            </a>
            <button type="button" className="wh-row" data-wh-row="resources" onClick={() => showScreen("resources")}>
              <span>
                <span className="wh-row-t">{portalText("core", "site_header.resources")}</span>
                <span className="wh-row-s">{portalText("core", "site_header.resources_sub")}</span>
              </span>
              <RowIcon d="M9 18l6-6-6-6" />
            </button>
          </div>

          <div className="wh-scr" data-wh-screen="solutions" hidden={screen !== "solutions"}>
            {screenTop("solutions", "site_header.solutions")}
            <div className="wh-tabs" role="tablist" aria-label={portalText("core", "site_header.solutions")}>
              {SOLUTION_TAB.map((column) => (
                <button
                  key={column.id}
                  type="button"
                  role="tab"
                  className="wh-tab"
                  id={`wh-tab-${column.id}`}
                  data-wh-tab={column.id}
                  aria-selected={tab === column.id}
                  aria-controls={`wh-sol-${column.id}`}
                  onClick={() => setTab(column.id)}
                >
                  {portalText("core", column.label)}
                </button>
              ))}
            </div>
            {SOLUTION_TAB.map((column) => (
              <div
                key={column.id}
                id={`wh-sol-${column.id}`}
                className="wh-tabpanel"
                role="tabpanel"
                aria-labelledby={`wh-tab-${column.id}`}
                data-wh-tabpanel={column.id}
                hidden={tab !== column.id}
              >
                {column.entries
                  .filter((entry) => entry.status === "live")
                  .map((entry) => (
                    <a key={entry.id} href={site(entry.path)} className="wh-mrow">
                      <span className="wh-tile">
                        <svg className="wh-icon" stroke="currentColor" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                          {HEADER_ICON[entry.icon].map((segment) => (
                            <path key={segment} d={segment} />
                          ))}
                        </svg>
                      </span>
                      <span className="wh-mrow-name">{solutionName(entry)}</span>
                      <span className="wh-live">
                        <span className="wh-dot" />
                        {portalText("core", "site_header.live")}
                      </span>
                    </a>
                  ))}
                {column.entries.some((entry) => entry.status === "soon") ? (
                  <p className="wh-csoon">{portalText("core", "site_header.coming_soon")}</p>
                ) : null}
                {column.entries
                  .filter((entry) => entry.status === "soon")
                  .map((entry) => (
                    <a key={entry.id} href={site(entry.path)} className="wh-mrow wh-mrow-soon" aria-haspopup="dialog" onClick={joinWaitlist(entry, "nav_mobile")}>
                      <span className="wh-tile">
                        <svg className="wh-icon" stroke="currentColor" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                          {HEADER_ICON[entry.icon].map((segment) => (
                            <path key={segment} d={segment} />
                          ))}
                        </svg>
                      </span>
                      <span className="wh-mrow-name">{solutionName(entry)}</span>
                      <span className="wh-wait">{portalText("core", "site_header.waitlist")}</span>
                    </a>
                  ))}
              </div>
            ))}
            <a href={site(HEADER_LIBRARY_PATH)} className="wh-mall">
              {portalText("core", "site_header.see_all")}
            </a>
          </div>

          <div className="wh-scr" data-wh-screen="product" hidden={screen !== "product"}>
            {screenTop("product", "site_header.product")}
            <div className="wh-plist">
              {HEADER_PRODUCT_NARROW.map((entry) => (
                <a key={entry.id} href={site(entry.path)} className="wh-prow">
                  {linkName(entry)}
                </a>
              ))}
            </div>
          </div>

          <div className="wh-scr" data-wh-screen="resources" hidden={screen !== "resources"}>
            {screenTop("resources", "site_header.resources")}
            <div className="wh-plist">
              {HEADER_RESOURCE.map((entry) => (
                <a key={entry.id} href={site(entry.path)} className="wh-prow">
                  {linkName(entry)}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="wh-mfoot">
          {signedIn || screen !== "main" ? null : (
            <Link to="/log-in" className="wh-mlogin">
              {portalText("core", "auth.log_in_f7c400")}
            </Link>
          )}
          {signedIn ? null : (
            <Link to="/sign-up" className="wh-mgo">
              {portalText("core", "site_header.start_free_30")}
            </Link>
          )}
          {/* Drawn while the menu is open on its first screen, so the list is closed whenever the menu or the screen changes. */}
          {mobileOpen && screen === "main" ? (
            <div>
              <HeaderLanguageMenu menuId="wh-m-language" />
            </div>
          ) : null}
        </div>
      </div>

      {waitlist === null ? null : (
        <WaitlistDialog
          key={`${waitlist.source}:${waitlist.entry.id}`}
          opening={waitlist}
          onClose={() => {
            setWaitlist(null);
          }}
        />
      )}
    </header>
  );
}
